import React from 'react'
import { useClinic } from './clinic-context'
import { useUserContext } from '~config/user-context'
import { eqStringFilter, gtFloatFilter } from '~utils/query-helpers'
import {
    useCountInternalChatUsersQuery,
    UserNotificationSound,
} from '~graphql/generated/graphql'
import { configureAbly, useChannel } from '@ably-labs/react-hooks'
import { ChannelTypeNames, constructChannel } from '~utils/ably-helpers'
import { AUDIO_SOUNDS, playAudio } from '~utils/audio-helpers'
import {
    DESKTOP_NOTIFICATION_TAGS,
    showDesktopNotification,
} from '~utils/desktop-notifications'

export function useUnreadInternalChats() {
    return React.useContext(UnreadInternalChatsContext)
}

export default function UnreadInternalChatsProvider({
    children,
}: React.PropsWithChildren) {
    const { user } = useUserContext()
    const { clinic, clinicGroup } = useClinic()

    const clinicEntityFilter = clinic
        ? eqStringFilter('internal_chat.clinic', clinic.id)
        : clinicGroup
          ? eqStringFilter('internal_chat.clinic_group', clinicGroup.id)
          : undefined

    const { data, refetch: refreshUnreadInternalChats } =
        useCountInternalChatUsersQuery({
            variables: {
                where: clinicEntityFilter
                    ? [
                          clinicEntityFilter,
                          eqStringFilter('user', user?.id || ''),
                          gtFloatFilter('unread_message_count', 0),
                      ]
                    : [
                          eqStringFilter('user', user?.id || ''),
                          gtFloatFilter('unread_messages_count', 0),
                      ],
            },
            skip: (!clinic && !clinicGroup) || !user,
        })

    const unreadInternalChats = data?.countInternalChatUsers || 0

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    useChannel(
        constructChannel(ChannelTypeNames.teamChatNotification, user?.id || ''),
        () => {
            if (
                user?.enabled_notification_sounds?.includes(
                    UserNotificationSound.TeamChat,
                )
            ) {
                playAudio(AUDIO_SOUNDS.teamChat)
                // The server only publishes `{ unreadMessageCount }` here (no
                // message content), so this stays a generic ping.
                showDesktopNotification('New team chat message', {
                    tag: DESKTOP_NOTIFICATION_TAGS.teamChat,
                })
            }

            refreshUnreadInternalChats()
        },
    )

    return (
        <UnreadInternalChatsContext.Provider
            value={{
                unreadInternalChats,
                refreshUnreadInternalChats,
            }}
        >
            {children}
        </UnreadInternalChatsContext.Provider>
    )
}

type UnreadInternalChatsContextType = {
    unreadInternalChats: number
    refreshUnreadInternalChats: () => void
}

const UnreadInternalChatsContext =
    React.createContext<UnreadInternalChatsContextType>({
        unreadInternalChats: 0,
        refreshUnreadInternalChats: () => {},
    })
